/* eslint-disable @typescript-eslint/no-use-before-define */

import {useMemo, useLayoutEffect, useState, useRef, useCallback} from 'react';
import type {
  Collection as ReactAriaCollection,
  Key,
  Node,
  FocusStrategy,
  Selection,
} from '@react-types/shared';
import type {ComboBoxStateOptions as ReactAriaComboBoxStateOptions} from './useComboBoxState';
import {useMenuTriggerState} from '../menu';
import {useControlledState} from '../utils';
import {toReactAriaCollection} from '../../collections/state/useReactAriaTreeState';
import {TreeCollection} from '../../collections/state/ReactAriaTreeCollection';
import type {Collection, Item} from '../../collections/types';
import type {
  MultiSelectStateOptions,
  MultiSelectState,
} from '../select/useMultiSelectState';
import {useMultiSelectState} from '../select/useMultiSelectState';
import type {
  TreeState,
  Filterable,
  FilterFunction,
} from '../../collections/state/types';

export interface MultiComboBoxState extends MultiSelectState {
  /** The current value of the combo box input. */
  inputValue: string;
  /** Sets the value of the combo box input. */
  setInputValue(value: string): void;
  /** Selects the currently focused item and updates the input value. */
  commit(): void;
  /** Opens the menu. */
  open(focusStrategy?: FocusStrategy | null, trigger?: MenuTriggerAction): void;
  /** Toggles the menu. */
  toggle(
    focusStrategy?: FocusStrategy | null,
    trigger?: MenuTriggerAction,
  ): void;
  /** Resets the input value to the previously selected item's text if any and closes the menu.  */
  revert(): void;
}

type MenuTriggerAction = ReactAriaComboBoxStateOptions<unknown>['menuTrigger'];

export interface MultiComboBoxOptions {
  /** Whether the ComboBox allows a non-item matching input value to be set. */
  allowsCustomValue?: boolean;
  /**
   * The interaction required to display the ComboBox menu.
   * @default 'input'
   */
  menuTrigger?: MenuTriggerAction;

  /** The value of the ComboBox input (controlled). */
  inputValue?: string;
  /** The default value of the ComboBox input (uncontrolled). */
  defaultInputValue?: string;
  /** Handler that is called when the ComboBox input value changes. */
  onInputChange?: (value: string) => void;
}

export interface MultiComboBoxStateOptions<T>
  extends MultiSelectStateOptions,
    MultiComboBoxOptions {
  filter?: Filterable<T>;
  /** Whether the combo box allows the menu to be open when the collection is empty. */
  allowsEmptyCollection?: boolean;
  /** Whether the combo box menu should close on blur. */
  shouldCloseOnBlur?: boolean;
}

/**
 * A replacement for
 * [useComboBoxState](https://react-spectrum.adobe.com/react-stately/useComboBoxState.html)
 * from react-stately that supports tree-lists and Sail's collections. Pass `getItems` from `useCollection` in here to produce a
 * list of items.
 */
export function useMultiComboBoxState<T>({
  filter = {},
  selection,
  trigger,
  expandable,
  ...props
}: MultiComboBoxStateOptions<T>): MultiComboBoxState & TreeState {
  const {defaultFilter} = filter;
  const {
    getItems,
    menuTrigger = 'input',
    allowsEmptyCollection = false,
    allowsCustomValue,
    shouldCloseOnBlur = true,
  } = props;

  const [showAllItems, setShowAllItems] = useState(false);
  const [isFocused, setFocusedState] = useState(false);

  const isSingleSelection = selection.selectionMode === 'single';

  const mapSelectionToKeysSet = useCallback(
    (selection: 'all' | Iterable<Key> | undefined): Set<Key> => {
      let allKeys: Selection = new Set();
      if (selection === 'all') {
        allKeys = new Set(getItems().map(({key}) => key));
      } else if (selection) {
        allKeys = new Set(selection);
      }

      return allKeys;
    },
    [getItems],
  );

  const getFirstKey = (
    keys: 'all' | Iterable<Key> | undefined,
  ): Key | null | undefined => {
    if (keys === undefined) {
      return undefined;
    }
    const keysArray: Key[] = [...mapSelectionToKeysSet(keys)];
    return keysArray?.[0] ?? null;
  };

  const {
    collection,
    selectionManager,
    selectedKeys,
    setSelectedKeys,
    selectedItems,
    disabledKeys,
    ...selectState
  } = useMultiSelectState({
    getItems,
    selection,
    trigger,
    expandable,
  });

  const [inputValue, setInputValue] = useControlledState<string>(
    props.inputValue,
    props.defaultInputValue ??
      (isSingleSelection ? selectedItems?.[0]?.textValue : '') ??
      '',
    props.onInputChange,
  );

  const controlledSelectedKey = getFirstKey(selection.selectedKeys);
  const firstSelectedKey = getFirstKey(selectedKeys);

  // Preserve original collection so we can show all items on demand
  const originalCollection = collection;
  const filteredCollection = useMemo(
    () =>
      !defaultFilter
        ? collection
        : filterCollection(getItems, inputValue, defaultFilter),
    [collection, inputValue, defaultFilter, getItems],
  );
  const [lastCollection, setLastCollection] = useState(filteredCollection);

  // Track what action is attempting to open the menu
  const menuOpenTrigger = useRef<MenuTriggerAction | undefined>('focus');
  const onOpenChange = (open: boolean) => {
    if (trigger.onOpenChange) {
      trigger.onOpenChange(open);
    }

    selectionManager.setFocused(open);
    if (!open) {
      selectionManager.setFocusedKey(null!);
    }
  };

  const triggerState = useMenuTriggerState({
    ...props,
    onOpenChange,
  });
  const open = (focusStrategy?: FocusStrategy, trigger?: MenuTriggerAction) => {
    const displayAllItems =
      trigger === 'manual' || (trigger === 'focus' && menuTrigger === 'focus');
    // Prevent open operations from triggering if there is nothing to display
    // Also prevent open operations from triggering if items are uncontrolled but defaultItems is empty, even if displayAllItems is true.
    // This is to prevent comboboxes with empty defaultItems from opening but allow controlled items comboboxes to open even if the initial list is empty (assumption is user will provide swap the empty list with a base list via onOpenChange returning `menuTrigger` manual)
    if (
      allowsEmptyCollection ||
      filteredCollection.size > 0 ||
      (displayAllItems && originalCollection.size > 0)
    ) {
      if (
        displayAllItems &&
        !triggerState.isOpen &&
        (isSingleSelection || (!isSingleSelection && inputValue === ''))
      ) {
        // Show all items if menu is manually opened. Only care about this if items are undefined
        setShowAllItems(true);
      }

      menuOpenTrigger.current = trigger;
      triggerState.open(focusStrategy);
    }
  };

  const toggle = (
    focusStrategy?: FocusStrategy,
    trigger?: MenuTriggerAction,
  ) => {
    const displayAllItems =
      trigger === 'manual' || (trigger === 'focus' && menuTrigger === 'focus');
    // If the menu is closed and there is nothing to display, early return so toggle isn't called to prevent extraneous onOpenChange
    if (
      !(
        allowsEmptyCollection ||
        filteredCollection.size > 0 ||
        (displayAllItems && originalCollection.size > 0)
      ) &&
      !triggerState.isOpen
    ) {
      return;
    }

    if (
      displayAllItems &&
      !triggerState.isOpen &&
      (isSingleSelection || (!isSingleSelection && inputValue === ''))
    ) {
      // Show all items if menu is toggled open. Only care about this if items are undefined
      setShowAllItems(true);
    }

    // Only update the menuOpenTrigger if menu is currently closed
    if (!triggerState.isOpen) {
      menuOpenTrigger.current = trigger;
    }

    toggleMenu(focusStrategy);
  };

  // If menu is going to close, save the current collection so we can freeze the displayed collection when the
  // user clicks outside the popover to close the menu. Prevents the menu contents from updating as the menu closes.
  let toggleMenu = useCallback(
    (focusStrategy?: FocusStrategy) => {
      if (triggerState.isOpen) {
        setLastCollection(filteredCollection);
      }

      triggerState.toggle(focusStrategy);
    },
    [triggerState, filteredCollection],
  );

  const closeMenu = useCallback(() => {
    if (triggerState.isOpen) {
      setLastCollection(filteredCollection);
      triggerState.close();
    }
  }, [triggerState, filteredCollection]);

  const lastValue = useRef(inputValue);
  const resetInputValue = () => {
    const itemText =
      collection.getItem(firstSelectedKey ?? '')?.textValue ?? '';
    lastValue.current = itemText;
    setInputValue(itemText);
  };

  const lastSelectedKey = useRef<Key | undefined | null>(
    isSingleSelection
      ? controlledSelectedKey ??
          getFirstKey(selection.defaultSelectedKeys) ??
          null
      : null,
  );
  const lastSelectedKeyText = useRef(
    isSingleSelection
      ? collection.getItem(firstSelectedKey ?? '')?.textValue ?? ''
      : '',
  );
  // intentional omit dependency array, want this to happen on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    // Open and close menu automatically when the input value changes if the input is focused,
    // and there are items in the collection or allowEmptyCollection is true.
    if (
      isFocused &&
      (filteredCollection.size > 0 || allowsEmptyCollection) &&
      !triggerState.isOpen &&
      inputValue !== lastValue.current &&
      inputValue !== '' &&
      menuTrigger !== 'manual'
    ) {
      open(undefined, 'input');
    }

    // Close the menu if the collection is empty. Don't close menu if filtered collection size is 0
    // but we are currently showing all items via button press
    if (
      !showAllItems &&
      !allowsEmptyCollection &&
      triggerState.isOpen &&
      filteredCollection.size === 0
    ) {
      closeMenu();
    }

    // Close when an item is selected.
    if (
      isSingleSelection &&
      firstSelectedKey != null &&
      firstSelectedKey !== lastSelectedKey.current
    ) {
      closeMenu();
    }

    // Clear focused key when input value changes and display filtered collection again.
    if (inputValue !== lastValue.current) {
      selectionManager.setFocusedKey(null!);
      setShowAllItems(false);

      // Set selectedKeys to empty when the user clears the input.
      // If controlled, this is the application developer's responsibility.
      if (
        isSingleSelection &&
        inputValue === '' &&
        (props.inputValue === undefined || controlledSelectedKey === undefined)
      ) {
        setSelectedKeys(new Set());
      }
    }

    // If the selectedKey changed, update the input value.
    // Do nothing if both inputValue and selectedKey are controlled.
    // In this case, it's the user's responsibility to update inputValue in onSelectionChange.
    if (
      isSingleSelection &&
      firstSelectedKey !== lastSelectedKey.current &&
      (props.inputValue === undefined || controlledSelectedKey === undefined)
    ) {
      resetInputValue();
    } else {
      lastValue.current = inputValue;
    }

    if (isSingleSelection) {
      // Update the inputValue if the selected item's text changes from its last tracked value.
      // This is to handle cases where a selectedKey is specified but the items aren't available (async loading) or the selected item's text value updates.
      // Only reset if the user isn't currently within the field so we don't erroneously modify user input.
      // If inputValue is controlled, it is the user's responsibility to update the inputValue when items change.
      const selectedItemText =
        collection.getItem(firstSelectedKey ?? '')?.textValue ?? '';
      if (
        !isFocused &&
        firstSelectedKey != null &&
        props.inputValue === undefined &&
        firstSelectedKey === lastSelectedKey.current
      ) {
        if (lastSelectedKeyText.current !== selectedItemText) {
          lastValue.current = selectedItemText;
          setInputValue(selectedItemText);
        }
      }

      lastSelectedKey.current = firstSelectedKey;
      lastSelectedKeyText.current = selectedItemText;
    }
  });

  // Revert input value and close menu
  const revert = () => {
    if (
      allowsCustomValue &&
      (!isSingleSelection || firstSelectedKey === null)
    ) {
      commitCustomValue();
    } else {
      commitSelection();
    }
  };

  let commitCustomValue = () => {
    if (isSingleSelection) {
      lastSelectedKey.current = null;
      setSelectedKeys(new Set());
    }
    closeMenu();
  };

  let commitSelection = () => {
    // If multiple things are controlled, call onSelectionChange
    if (controlledSelectedKey !== undefined && props.inputValue !== undefined) {
      selection.onSelectionChange?.(mapSelectionToKeysSet(selectedKeys));

      if (isSingleSelection) {
        // Stop menu from reopening from useEffect
        const itemText =
          collection.getItem(firstSelectedKey ?? '')?.textValue ?? '';
        lastValue.current = itemText;
        closeMenu();
      }
    } else {
      // If only a single aspect of combobox is controlled, reset input value and close menu for the user
      if (isSingleSelection) {
        resetInputValue();
      }
      closeMenu();
    }
  };

  const commitValue = () => {
    if (isSingleSelection && allowsCustomValue) {
      const itemText =
        collection.getItem(firstSelectedKey ?? '')?.textValue ?? '';
      if (inputValue === itemText) {
        commitSelection();
      } else {
        commitCustomValue();
      }
    } else {
      // Reset inputValue and close menu
      commitSelection();
    }
  };

  const commit = () => {
    if (triggerState.isOpen && selectionManager.focusedKey != null) {
      // Reset inputValue and close menu here if the selected key is already the focused key. Otherwise
      // fire onSelectionChange to allow the application to control the closing.
      if (isSingleSelection) {
        if (firstSelectedKey === selectionManager.focusedKey) {
          commitSelection();
        } else {
          setSelectedKeys(new Set([selectionManager.focusedKey]));
        }
      } else {
        const mappedSelectedKeys = mapSelectionToKeysSet(selectedKeys);

        const isFocusedKeySelected = [...mappedSelectedKeys].some(
          (key) => key === selectionManager.focusedKey,
        );

        if (!isFocusedKeySelected) {
          setSelectedKeys(
            new Set([...mappedSelectedKeys, selectionManager.focusedKey]),
          );
        } else {
          setSelectedKeys(
            new Set(
              [...mappedSelectedKeys].filter(
                (key) => key !== selectionManager.focusedKey,
              ),
            ),
          );
        }
      }
    } else {
      commitValue();
    }
  };

  const setFocused = (isFocused: boolean) => {
    if (isFocused) {
      if (menuTrigger === 'focus') {
        open(undefined, 'focus');
      }
    } else if (shouldCloseOnBlur) {
      commitValue();
    }

    setFocusedState(isFocused);
  };

  const displayedCollection = useMemo(() => {
    if (triggerState.isOpen) {
      if (showAllItems) {
        return originalCollection;
      } else {
        return filteredCollection;
      }
    } else {
      return lastCollection;
    }
  }, [
    triggerState.isOpen,
    originalCollection,
    filteredCollection,
    showAllItems,
    lastCollection,
  ]);

  const expandedKeys =
    expandable && inputValue
      ? new Set(collection.getKeys() as string[])
      : selectState.expandedKeys;

  return {
    // NOTE: select state has it's own menu trigger that we're overriding here
    ...selectState,
    ...triggerState,
    expandedKeys,
    toggle,
    open,
    close: commitValue,
    selectionManager,
    selectedKeys,
    setSelectedKeys,
    disabledKeys,
    isFocused,
    setFocused,
    selectedItems,
    collection: displayedCollection,
    inputValue,
    setInputValue,
    commit,
    revert,
  };
}

function filterCollection<T>(
  getItems: Collection['getItems'],
  inputValue: string,
  filter: FilterFunction<T>,
): ReactAriaCollection<Node<unknown>> {
  const collection = toReactAriaCollection(() =>
    getItems((item: Item, userItem: T) => {
      return filter(item.textValue ?? '', inputValue, userItem);
    }),
  );

  return new TreeCollection(collection);
}
